import type {
  FbContentType,
  IgContentType,
  PostPlatform,
  PostPlatformTarget,
  PostSummaryTarget,
  PostTargetInput,
} from '../../types/publishing.js';

// Publishing — the DESTINATION catalogue (composer redesign, 2026-07-20).
//
// A "destination" is what the AM ticks in the composer: Facebook, Instagram,
// Google Business, Facebook Story, Instagram Story. A destination is
// (platform × placement) — the same connected page can receive BOTH a feed post
// and a story from one composition.
//
// ⚠️ THE SHAPE RULE (decided in the calendar fan-out brief, 2026-07-19, and
// implemented here): multi-placement per platform lands as ADDITIONAL ENTRIES in
// `targets[]` — a second facebook target carrying contentType 'story' — NEVER a
// nested `placements[]` inside one target. The calendar's fan-out expands
// strictly one row per targets[] ENTRY and has a test pinning the
// two-targets-same-platform case; a nested array would force that expansion to
// become target × placement everywhere it is consumed.
//
// PLACEMENT IS DERIVED, NOT STORED SEPARATELY. The existing
// `fbContentType`/`igContentType` fields already carry 'story', so a destination
// round-trips through the SAME stored shape with no schema addition — 'story'
// means the story destination, anything else means the feed destination. (Reel
// stays a sub-choice INSIDE the feed destination: a Reel is a feed-surface post,
// not a separate tickable destination.)

export type PostPlacement = 'feed' | 'story';

export type PostDestinationId =
  | 'facebook'
  | 'instagram'
  | 'googlebusiness'
  | 'facebook_story'
  | 'instagram_story';

export interface PostDestination {
  id: PostDestinationId;
  platform: PostPlatform;
  placement: PostPlacement;
  /** Full label for the options block heading. */
  label: string;
  /**
   * Compact PLATFORM-only label for the tick row + counter row. Deliberately
   * NOT placement-qualified ('Facebook', not 'FB Story') — every surface that
   * renders it also renders the placement badge, so baking the placement in
   * here would read as "FB Story Story".
   */
  shortLabel: string;
  /** Stories are ephemeral: no first comment, no GBP config. */
  supportsFirstComment: boolean;
}

// Display order — the tick row, the options blocks, the schedule rows and the
// previews all read this ONE array so they can never drift out of order.
export const DESTINATIONS: PostDestination[] = [
  {
    id: 'facebook',
    platform: 'facebook',
    placement: 'feed',
    label: 'Facebook',
    shortLabel: 'Facebook',
    supportsFirstComment: true,
  },
  {
    id: 'instagram',
    platform: 'instagram',
    placement: 'feed',
    label: 'Instagram',
    shortLabel: 'Instagram',
    supportsFirstComment: true,
  },
  {
    id: 'googlebusiness',
    platform: 'googlebusiness',
    placement: 'feed',
    label: 'Google Business',
    shortLabel: 'Google',
    supportsFirstComment: false,
  },
  {
    id: 'facebook_story',
    platform: 'facebook',
    placement: 'story',
    label: 'Facebook Story',
    shortLabel: 'Facebook',
    // Zernio/Meta skip firstComment on stories (see platformRules + postMapper).
    supportsFirstComment: false,
  },
  {
    id: 'instagram_story',
    platform: 'instagram',
    placement: 'story',
    label: 'Instagram Story',
    shortLabel: 'Instagram',
    supportsFirstComment: false,
  },
];

export const DESTINATION_ORDER: PostDestinationId[] = DESTINATIONS.map((d) => d.id);

const BY_ID = new Map<PostDestinationId, PostDestination>(DESTINATIONS.map((d) => [d.id, d]));

export function destinationById(id: PostDestinationId): PostDestination {
  const d = BY_ID.get(id);
  if (!d) throw new Error(`Unknown publishing destination: ${id}`);
  return d;
}

/** Which destinations a connected-platform map makes available. */
export function availableDestinations(
  connected: Record<PostPlatform, boolean>,
): PostDestination[] {
  return DESTINATIONS.filter((d) => connected[d.platform]);
}

// ---------------------------------------------------------------------------
// target ⇄ destination
// ---------------------------------------------------------------------------

/**
 * The placement a stored/summary target represents. Derived from the platform's
 * own content type — 'story' is the only value that changes the surface.
 */
export function placementOfTarget(
  target: Pick<PostSummaryTarget, 'platform' | 'igContentType' | 'fbContentType'>,
): PostPlacement {
  if (target.platform === 'facebook') return target.fbContentType === 'story' ? 'story' : 'feed';
  if (target.platform === 'instagram') return target.igContentType === 'story' ? 'story' : 'feed';
  return 'feed';
}

/** The destination a stored/summary target belongs to. */
export function destinationOfTarget(
  target: Pick<PostSummaryTarget, 'platform' | 'igContentType' | 'fbContentType'>,
): PostDestination {
  const placement = placementOfTarget(target);
  const found = DESTINATIONS.find(
    (d) => d.platform === target.platform && d.placement === placement,
  );
  // Every (platform, placement) pair in the model has a destination; the fallback
  // keeps this total for a defensively-malformed stored target.
  return found ?? destinationById(target.platform as PostDestinationId);
}

/**
 * The STABLE IDENTITY of a target within a post: platform + placement.
 *
 * ⚠️ Load-bearing. Before multi-placement, every server-side merge keyed targets
 * by PLATFORM alone (the dispatch-outcome fold, the stale-PATCH status re-merge,
 * the webhook status mirror). With two facebook targets those maps silently
 * collapse — the story's publish result would overwrite the feed's. Every one of
 * those call sites now keys on this instead.
 */
export function targetKey(
  target: Pick<PostSummaryTarget, 'platform' | 'igContentType' | 'fbContentType'>,
): string {
  return `${target.platform}:${placementOfTarget(target)}`;
}

/** The content-type fields a destination contributes to its target. */
export function contentTypeForDestination(
  destination: PostDestination,
  feedContentType: { fb: FbContentType; ig: IgContentType },
): { fbContentType?: FbContentType; igContentType?: IgContentType } {
  if (destination.platform === 'facebook') {
    return { fbContentType: destination.placement === 'story' ? 'story' : feedContentType.fb };
  }
  if (destination.platform === 'instagram') {
    return { igContentType: destination.placement === 'story' ? 'story' : feedContentType.ig };
  }
  return {};
}

/**
 * The per-target effective schedule: a target's own `scheduledFor` when it
 * carries one, else the post-level value (§F2 — Zernio's own
 * `platforms[].scheduledFor` semantics: "when omitted, the top-level
 * scheduledFor is used").
 */
export function effectiveTargetScheduledFor(
  postScheduledFor: string | null,
  target: Pick<PostTargetInput | PostPlatformTarget | PostSummaryTarget, 'scheduledFor'>,
): string | null {
  const own = target.scheduledFor;
  return typeof own === 'string' && own !== '' ? own : postScheduledFor;
}
